import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { jobsApi } from "@/api/client";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { useToast } from "@/components/toast/ToastProvider";
import { UndeliveredNotice, undeliveredRecords } from "@/pages/pipelineBacklog";
import type { PositionView } from "@/types/api";
import {
  newResetOpId,
  type ResetSummary,
  resetOutcomeMessage,
  resetSummary,
} from "./positionReset";

interface ResetPositionModalProps {
  isOpen: boolean;
  job: string;
  onClose: () => void;
}

/**
 * Confirms a position reset. Before asking, the dialog says what the reset
 * deletes (the saved checkpoints, a scanner's scan position) and where the
 * next start then reads from, in the engine's own words.
 *
 * A reset is one operation until it succeeds: its id is minted the first time
 * the dialog opens and kept across retries and reopenings, so trying again
 * after a failure or a lost reply continues that reset. The engine refuses a
 * different operation while an unfinished one holds the pipeline.
 *
 * A pipeline still holding undelivered records is refused too. The dialog
 * then names how many a reset would discard, and only a second confirmation
 * discards them.
 */
export function ResetPositionModal({
  isOpen,
  job,
  onClose,
}: ResetPositionModalProps) {
  const reset = usePositionReset(isOpen, job, onClose);
  const { position, error, undelivered } = reset;

  return (
    <ConfirmDeleteModal
      isOpen={isOpen}
      title={`Reset position of "${job}"?`}
      confirmLabel={
        undelivered === null ? "Reset position" : "Reset and discard"
      }
      loading={reset.resetting || (!position && !error)}
      loadingLabel={reset.resetting ? "Resetting…" : "Reading…"}
      error={error}
      message={
        position ? (
          <>
            <ResetMessage
              summary={resetSummary(position)}
              now={position.next_start}
            />
            {undelivered !== null && (
              <p>
                <UndeliveredNotice
                  subject="The pipeline"
                  action="Resetting"
                  records={undelivered}
                />
              </p>
            )}
          </>
        ) : (
          "Reading where the pipeline got to…"
        )
      }
      onConfirm={reset.confirm}
      onCancel={onClose}
    />
  );
}

// The dialog's state: the position read when it opens, and the one reset
// operation it carries until that succeeds.
function usePositionReset(isOpen: boolean, job: string, onClose: () => void) {
  const [opId, setOpId] = useState("");
  const [position, setPosition] = useState<PositionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [undelivered, setUndelivered] = useState<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isOpen) return;
    setOpId((current) => current || newResetOpId());
    setPosition(null);
    setError(null);
    setUndelivered(null);
    jobsApi
      .getPosition(job)
      .then(setPosition)
      .catch((err: unknown) =>
        setError(errorText(err, "Could not read the position")),
      );
  }, [isOpen, job]);

  // Kept open with the same operation id on failure, so trying again
  // continues it. A refusal for undelivered records asks again instead.
  const failed = (err: unknown) => {
    const records = undeliveredRecords(err);
    if (records === undefined) setError(errorText(err, "The reset failed"));
    else setUndelivered(records);
  };

  const confirm = async () => {
    setResetting(true);
    setError(null);
    try {
      const outcome = await jobsApi.resetPosition(job, {
        opId,
        discardBacklog: undelivered !== null,
      });
      setOpId("");
      queryClient.invalidateQueries();
      toast({ state: "Confirm", message: resetOutcomeMessage(outcome) });
      onClose();
    } catch (err) {
      failed(err);
    } finally {
      setResetting(false);
    }
  };

  return { position, error, resetting, undelivered, confirm };
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function ResetMessage({
  summary,
  now,
}: {
  summary: ResetSummary;
  now: string;
}) {
  if (summary.refused) return <p>Can't reset: {summary.refused}.</p>;
  return (
    <>
      <p>{summary.removes}</p>
      <p>
        Next start: {summary.nextStart}.
        {summary.consequence && ` ${summary.consequence}`}
      </p>
      <p>Now it {now}.</p>
    </>
  );
}
